import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import ReferralInfoContext from './ReferralInfoContext';
import { fetchProducts } from '../api/storefront';
import referralTemplate from '../util/template-parser';
import useFeatureFlags from '../hooks/use-feature-flags';
import LocationContext from './LocationContext/LocationContext';
import { CountryCodes } from '../constants';

const MessageCenterContext = React.createContext(null);

const MessageCenterProvider = ({ children }) => {
  const featureFlags = useFeatureFlags();
  const internationalSales = featureFlags['space-dogs'];
  const { country, hasFetched } = useContext(LocationContext);

  const [messages, setMessagesState] = useState([]);
  const { referralInfo } = useContext(ReferralInfoContext);

  const setMessages = (newMessages) => {
    if (!Array.isArray(newMessages)) {
      throw new Error('messages must be an array');
    }

    setMessagesState(
      newMessages.sort((a, b) => b.fields?.priority - a.fields?.priority)
    );
  };

  const templateReferralBanner = (message) => {
    if (referralInfo?.isReferral) {
      return fetchProducts().then((products) =>
        referralTemplate(referralInfo, products, message)
      );
    }
    return Promise.resolve(null);
  };

  const getMessage = async () => {
    for (let i = 0; i < messages.length; i += 1) {
      const message = messages[i];
      const title = message.fields?.title;
      const color = `${message?.fields?.backgroundColor?.fields?.hue}.${message?.fields?.backgroundColor?.fields?.tint}`;

      switch (title) {
        case 'Referral Info Banner': {
          // we do not know what region the user is in yet, so do not format the referral message.
          if (!hasFetched) break;

          const localizedMessage =
            internationalSales && country === CountryCodes.GB
              ? message.fields?.[`message${CountryCodes.GB}`] ??
                message.fields?.message
              : message.fields?.message;
          /* eslint-disable-next-line no-await-in-loop */
          const messageContent = await templateReferralBanner(localizedMessage);

          if (!messageContent) break;

          return {
            color,
            message: messageContent,
          };
        }
        default:
          return {
            color,
            message: message.fields?.message,
          };
      }
    }

    return null;
  };

  return (
    <MessageCenterContext.Provider
      value={{
        getMessage,
        setMessages,
      }}>
      {children}
    </MessageCenterContext.Provider>
  );
};

export const useMessageCenter = () => {
  const context = useContext(MessageCenterContext);
  if (context === undefined) {
    throw new Error(
      'useMessageCenter must be used within a MessageCenterProvider'
    );
  }
  return context;
};

MessageCenterProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default MessageCenterProvider;
